import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/cn";
import { useAuth } from "@/hooks/useAuth";
import { Container } from "./Container";

export function Navbar() {
  const { email, isAuthenticated, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      className={cn(
        "sticky top-0 z-40",
        "bg-surface-50/90 backdrop-blur-lg",
        "border-b border-surface-200"
      )}
    >
      <Container>
        <nav
          aria-label="Main navigation"
          className="flex h-16 items-center justify-between"
        >
          <Link
            to="/"
            className="font-serif font-light text-2xl text-surface-700 hover:opacity-80 transition-opacity"
          >
            wp<span className="text-primary-400">→</span>emdash
          </Link>

          <div className="hidden sm:flex items-center gap-8">
            <NavLinks />
            <Link
              to="/audit"
              className="uppercase text-[11px] tracking-[0.2em] font-sans font-medium text-primary-400 hover:text-primary-500 transition-colors"
            >
              Initiate
            </Link>
            {isAuthenticated && (
              <AuthControls email={email} logout={logout} />
            )}
          </div>

          <button
            type="button"
            aria-label="Toggle mobile menu"
            aria-expanded={mobileOpen}
            className="sm:hidden p-2 rounded-md text-surface-600 hover:bg-surface-100 transition-colors"
            onClick={() => setMobileOpen((prev) => !prev)}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {mobileOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </nav>
      </Container>

      {mobileOpen && (
        <div
          className={cn(
            "absolute top-full left-0 right-0 sm:hidden",
            "bg-surface-50",
            "border-b border-surface-200 shadow-sm"
          )}
        >
          <div className="flex flex-col py-2">
            <a
              href="/#how-it-works"
              className="px-6 py-3 uppercase text-[11px] tracking-[0.2em] font-sans font-medium text-surface-500 hover:text-primary-500 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              Benefits
            </a>
            <a
              href="/#pricing"
              className="px-6 py-3 uppercase text-[11px] tracking-[0.2em] font-sans font-medium text-surface-500 hover:text-primary-500 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              Pricing
            </a>
            <Link
              to="/audit"
              className="px-6 py-3 uppercase text-[11px] tracking-[0.2em] font-sans font-medium text-primary-400 hover:text-primary-500 transition-colors border-t border-surface-200 mt-1 pt-4"
              onClick={() => setMobileOpen(false)}
            >
              Initiate
            </Link>
            {isAuthenticated && (
              <>
                {email && (
                  <span className="px-6 py-3 font-sans text-xs text-surface-500 truncate border-t border-surface-200">
                    {email}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    setMobileOpen(false);
                  }}
                  className="px-6 py-3 text-sm text-left text-surface-500 hover:text-surface-700 transition-colors"
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function NavLinks() {
  return (
    <>
      <a
        href="/#how-it-works"
        className="uppercase text-[11px] tracking-[0.2em] font-sans font-medium text-surface-500 hover:text-primary-500 transition-colors"
      >
        Benefits
      </a>
      <a
        href="/#pricing"
        className="uppercase text-[11px] tracking-[0.2em] font-sans font-medium text-surface-500 hover:text-primary-500 transition-colors"
      >
        Pricing
      </a>
    </>
  );
}

interface AuthControlsProps {
  email: string | null;
  logout: () => void;
}

function AuthControls({ email, logout }: AuthControlsProps) {
  return (
    <div className="flex items-center gap-3 pl-4 border-l border-surface-200">
      {email && (
        <span className="font-sans text-xs text-surface-500 max-w-[160px] truncate">
          {email}
        </span>
      )}
      <button
        type="button"
        onClick={logout}
        className={cn(
          "uppercase text-[11px] tracking-[0.2em] font-sans font-medium",
          "text-surface-500 hover:text-surface-700 transition-colors"
        )}
      >
        Sign Out
      </button>
    </div>
  );
}
